import moment from 'moment';
import { getTimeSpanParsed, TimeDisplayFormat, TimeFormat } from '@/utils/time';
import { Button, Input } from 'reactstrap';
import 'moment/locale/de';
import Checkbox from '../Checkbox';
import {
  CheckBoxLabel,
  CheckBoxLabelContainer,
} from '../Checkbox/pickpackCheckbox';
import { TimeSlotRow } from '../TimePickerElements/styled';
import { IconButton, IconButtonDisplayLabelBelow } from '../Button/Button';
import { FaPlus, FaRegCalendarPlus, FaTrashAlt } from 'react-icons/all';
import { PrimeTimePicker } from '../TimePickerElements/PrimeTimePicker';
import i18n from '@/utils/i18n';
import { ThemeContext } from '../../providers/ThemeContextProvider';
import { useContext } from 'react';

export enum SpecialHoursType {
  OpeningHours,
  DeliveryHours,
}

type SpecialOpeningHoursMulti = {
  day: string;
  time: Array<string>;
};

type SpecialOpeningHoursPickerProps = {
  specialOpeningHoursMulti: Array<SpecialOpeningHoursMulti>;
  specialDeliveryHoursMulti: Array<SpecialOpeningHoursMulti>;
  onChange: Function;
};

export default function SpecialOpeningHoursPicker({
  specialOpeningHoursMulti,
  specialDeliveryHoursMulti,
  onChange,
}: SpecialOpeningHoursPickerProps) {
  const { isDarkMode } = useContext(ThemeContext);

  function findDay(
    specialOpeningsToSearch: Array<SpecialOpeningHoursMulti> = [],
    dayToFind: string,
  ) {
    const dayIndex = specialOpeningsToSearch.findIndex(
      (d) => d.day === dayToFind,
    );
    const dayToUpdate = specialOpeningsToSearch[dayIndex];
    return { dayIndex, dayToUpdate };
  }

  function updateTime(
    day: string,
    timeIndex: number,
    updatedValue: any,
    timeType: string,
    type = SpecialHoursType.OpeningHours,
  ) {
    console.log({ day, timeIndex, updatedValue, timeType });
    const isOpeningHours = type == SpecialHoursType.OpeningHours;
    const hours = isOpeningHours
      ? specialOpeningHoursMulti
      : specialDeliveryHoursMulti;
    let newSpecialHours = [...hours];
    const dayIndex = hours.findIndex((d) => d.day === day);
    const dayToUpdate = hours[dayIndex];

    // removing all text from input, gives null, ignore this
    if (updatedValue === null || !dayToUpdate) {
      return;
    }

    const parsedTimeSlot = getTimeSpanParsed(dayToUpdate.time[timeIndex]);
    let openingMoment = parsedTimeSlot.opens;
    let closingMoment = parsedTimeSlot.close;
    if (timeType === 'opens') {
      openingMoment = updatedValue;
    } else if (timeType === 'close') {
      closingMoment = updatedValue;
    } else {
      throw new Error(`unknown timeType ${timeType}`);
    }
    const newTime = [...dayToUpdate.time];
    newTime[timeIndex] = `${openingMoment.format(
      TimeFormat,
    )} - ${closingMoment.format(TimeFormat)}`;

    newSpecialHours[dayIndex] = { day, time: newTime };

    onChange(newSpecialHours, isOpeningHours);
  }

  function removeSlot(
    day: string,
    timeIndex: number,
    type = SpecialHoursType.OpeningHours,
  ) {
    const isOpeningHours = type == SpecialHoursType.OpeningHours;
    const hours = isOpeningHours
      ? specialOpeningHoursMulti
      : specialDeliveryHoursMulti;
    let newSpecialOpenings = [...hours];
    const dayIndex = hours.findIndex((d) => d.day === day);
    const dayToUpdate = newSpecialOpenings[dayIndex];
    // remove slot
    const newTime = [...dayToUpdate.time];
    newTime.splice(timeIndex, 1);
    newSpecialOpenings[dayIndex] = { day: dayToUpdate.day, time: newTime };
    onChange(newSpecialOpenings, isOpeningHours);
  }

  function onAddTimeslotClick(
    day: string,
    type = SpecialHoursType.OpeningHours,
  ) {
    const isOpeningHours = type == SpecialHoursType.OpeningHours;
    const hours = isOpeningHours
      ? specialOpeningHoursMulti
      : specialDeliveryHoursMulti;
    let specialOpenings = [...hours];
    const { dayIndex, dayToUpdate } = findDay(specialOpenings, day);
    const newTimeStr = '10:42AM - 11:42AM';
    if (dayToUpdate) {
      const newDay = {
        day: dayToUpdate.day,
        time: dayToUpdate.time.concat([newTimeStr]),
      };
      specialOpenings[dayIndex] = newDay;
    } else {
      specialOpenings.push({ day: day, time: [newTimeStr] });
    }
    onChange(specialOpenings, isOpeningHours);
  }

  function setOpenCloseOnDay(
    day: string,
    isClosed: boolean,
    type = SpecialHoursType.OpeningHours,
  ) {
    const isOpeningHours = type == SpecialHoursType.OpeningHours;
    const hours = isOpeningHours
      ? specialOpeningHoursMulti
      : specialDeliveryHoursMulti;
    const dayIndex = hours.findIndex((d) => d.day === day);
    const newSpecialOpenings = [...hours];
    newSpecialOpenings[dayIndex] = {
      day,
      time: isClosed ? ['closed'] : [],
    };
    onChange(newSpecialOpenings, isOpeningHours);
  }

  function addDay(type = SpecialHoursType.OpeningHours) {
    const isOpeningHours = type == SpecialHoursType.OpeningHours;
    const hours = isOpeningHours
      ? specialOpeningHoursMulti
      : specialDeliveryHoursMulti;
    const newSpecialOpenings = [...hours];
    let day = moment();
    // days should not be in the special openings array twice
    while (true) {
      const dayStr = day.format('YYYY-MM-DD');
      const { dayToUpdate: dayFound } = findDay(newSpecialOpenings, dayStr);
      if (!dayFound) {
        newSpecialOpenings.push({
          day: dayStr,
          time: ['10:42AM - 11:42AM'],
        });
        break;
      }
      day = moment(day).add(1, 'days');
    }
    onChange(newSpecialOpenings, isOpeningHours);
  }

  function removeDay(day: string, type = SpecialHoursType.OpeningHours) {
    const isOpeningHours = type == SpecialHoursType.OpeningHours;
    const hours = isOpeningHours
      ? specialOpeningHoursMulti
      : specialDeliveryHoursMulti;
    const newSpecialOpenings = [...hours];
    const { dayIndex } = findDay(newSpecialOpenings, day);
    newSpecialOpenings.splice(dayIndex, 1);
    onChange(newSpecialOpenings, isOpeningHours);
  }

  function updateDay(
    day: string,
    newDay: string,
    type = SpecialHoursType.OpeningHours,
  ) {
    const isOpeningHours = type == SpecialHoursType.OpeningHours;
    const hours = isOpeningHours
      ? specialOpeningHoursMulti
      : specialDeliveryHoursMulti;
    let newSpecialOpenings = [...hours];
    const { dayIndex, dayToUpdate } = findDay(newSpecialOpenings, day);
    newSpecialOpenings[dayIndex] = {
      ...dayToUpdate,
      day: newDay,
    };

    // filter out other entrys with the same day
    newSpecialOpenings = newSpecialOpenings.filter(
      (d, index) => index === dayIndex || d.day !== newDay,
    );

    onChange(newSpecialOpenings, isOpeningHours);
  }

  function renderSpecialDays(
    specialHours: SpecialOpeningHoursMulti[],
    type: SpecialHoursType,
  ) {
    return (specialHours || []).map((entry, i) => {
      const isClosed = !!(entry.time.length && entry.time[0] === 'closed');

      return (
        <div
          key={i}
          className="card flex-shrink-0 flex-grow-0"
          style={{ flexBasis: '270px' }}
        >
          <div className="card-header">
            <h5 style={{ textAlign: 'center' }}>
              {i18n.t(
                `OpeningHours:${
                  type == SpecialHoursType.OpeningHours
                    ? 'specialOpeningHours'
                    : 'specialDeliveryHours'
                }`,
              )}
            </h5>
            <div className="d-flex align-items-center">
              <Input
                type="date"
                value={entry.day ?? moment().format('YYYY-MM-DD')}
                name="special-opening-hours-day"
                onChange={(e) => {
                  console.log(e.target.value);
                  const newDate = new Date(e.target.value);
                  updateDay(
                    entry.day,
                    moment(newDate).format('YYYY-MM-DD'),
                    type,
                  );
                }}
              />
              <Button
                className="ms-2"
                color="danger"
                size="sm"
                onClick={() => removeDay(entry.day, type)}
              >
                <FaTrashAlt />
              </Button>
            </div>
          </div>
          <div className="card-body">
            <TimeSlotRow className="flex-grow-1">
              <div className="d-flex align-items-center flex-grow-1 flex-row">
                <div className="d-flex">
                  <div className="me-2">
                    <CheckBoxLabelContainer
                      onClick={() =>
                        setOpenCloseOnDay(entry.day, !isClosed, type)
                      }
                    >
                      <Checkbox checked={isClosed} />
                      <CheckBoxLabel
                        className={isDarkMode ? 'text-white' : 'text-black'}
                      >
                        {i18n.t('OpeningHours:closedAllday')}
                      </CheckBoxLabel>
                    </CheckBoxLabelContainer>
                  </div>
                </div>
              </div>
            </TimeSlotRow>
            {entry &&
              !isClosed &&
              entry.time.map((timeStr, i) => {
                const day = getTimeSpanParsed(timeStr);

                return (
                  <TimeSlotRow key={i} className="mt-3 flex-grow-1">
                    <div className="d-flex align-items-center flex-grow-1">
                      <div className="d-flex flex-row">
                        <div className="me-2">
                          <label
                            className={`me-1 mb-0 ${
                              isDarkMode ? 'text-white' : 'text-black'
                            }`}
                          >
                            {i18n.t('OpeningHours:opens')}:
                          </label>
                          <PrimeTimePicker
                            showSecond={false}
                            defaultValue={day ? day.opens : null}
                            value={day ? day.opens : null}
                            onChange={(newValue: any) =>
                              updateTime(entry.day, i, newValue, 'opens', type)
                            }
                            format={TimeDisplayFormat}
                            focusOnOpen={true}
                            use12Hours={false}
                            allowEmpty={false}
                          />
                        </div>
                        <div className="me-2">
                          <label
                            className={`me-1 mb-0 ${
                              isDarkMode ? 'text-white' : 'text-black'
                            }`}
                          >
                            {i18n.t('OpeningHours:closes')}:
                          </label>
                          <PrimeTimePicker
                            showSecond={false}
                            defaultValue={day ? day.close : null}
                            value={day ? day.close : null}
                            focusOnOpen={true}
                            onChange={(newValue: any) =>
                              updateTime(entry.day, i, newValue, 'close', type)
                            }
                            format={TimeDisplayFormat}
                            use12Hours={false}
                            allowEmpty={false}
                          />
                        </div>
                      </div>
                      <div className="align-self-end">
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => removeSlot(entry.day, i, type)}
                        >
                          <FaTrashAlt />
                        </Button>
                      </div>
                    </div>
                  </TimeSlotRow>
                );
              })}
          </div>
          <div className="card-footer">
            <IconButton
              disabled={isClosed}
              className="w-100"
              icon={<FaRegCalendarPlus />}
              label={i18n.t('OpeningHours:addTimeSlot')}
              onClick={() => onAddTimeslotClick(entry.day, type)}
            />
          </div>
        </div>
      );
    });
  }

  return (
    <div className="card-group d-flex flex-nowrap overflow-auto pb-3">
      <div
        className="card flex-shrink-0 flex-grow-0"
        style={{ flexBasis: '270px' }}
      >
        <div className="card-header">{i18n.t('OpeningHours:specialDays')}</div>
        <div className="card-body">
          {i18n.t('OpeningHours:specialDaysInfo')}
        </div>
        <div className="card-footer">
          <div className="mb-2">
            <IconButtonDisplayLabelBelow
              className="w-100"
              icon={<FaPlus />}
              onClick={() => addDay()}
              label={i18n.t('OpeningHours:specialOpeningHours')}
            />
          </div>
          <div>
            <IconButtonDisplayLabelBelow
              className="w-100"
              icon={<FaPlus />}
              onClick={() => addDay(SpecialHoursType.DeliveryHours)}
              label={i18n.t('OpeningHours:specialDeliveryHours')}
            />
          </div>
        </div>
      </div>
      {renderSpecialDays(
        specialOpeningHoursMulti,
        SpecialHoursType.OpeningHours,
      )}
      {renderSpecialDays(
        specialDeliveryHoursMulti,
        SpecialHoursType.DeliveryHours,
      )}
    </div>
  );
}
